import { ChangeDetectionStrategy, Component, HostListener, inject, input, output, signal } from '@angular/core';
import { SnackbarService } from "../../../../core/services/snackbar.service";
import { MatTooltip} from "@angular/material/tooltip";
import { MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { contentPaste, contentPasteGo } from "./configs/delayed-file-upload-card-config";
import { UploadProgressService } from "../../../../core/services/upload-progress.service";
import { DelayedFileUploadCardService } from "./services/delayed-file-upload-card.service";
import {
  DelayedFileUploadItemComponent
} from "./components/delayed-file-upload-item/delayed-file-upload-item.component";

@Component({
  selector: 'app-delayed-file-upload-card',
  imports: [
    MatTooltip,
    MatIconButton,
    MatIcon,
    DelayedFileUploadItemComponent
  ],
  templateUrl: './delayed-file-upload-card.component.html',
  styleUrl: './delayed-file-upload-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DelayedFileUploadCardComponent {
  allowedExtensions = input<ReadonlyArray<string>>([]);
  multiple = input<boolean>(false);
  filesSelectedForUpload = output<Array<File>>();

  public uploadProgressService: UploadProgressService = inject(UploadProgressService);
  private snackbarService: SnackbarService = inject(SnackbarService);
  private delayedFileUploadCardService: DelayedFileUploadCardService = inject(DelayedFileUploadCardService);

  public selectedFiles = signal<Array<File>>([]);
  public isHover = signal<boolean>(false);
  public isPasteMode = signal<boolean>(false);

  @HostListener('document:paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    this.isPasteMode() && this.handlePaste(event);
  }

  public get getClasses(): string {
    return this.isHover() ? 'hover': '';
  }

  public get getIcon(): string {
    return this.isPasteMode() ? contentPasteGo.icon : contentPaste.icon;
  }

  public getTooltip(): string {
    return this.isPasteMode() ? contentPasteGo.text : contentPaste.text;
  }

  public async handleFileDropped(event: DragEvent): Promise<void> {
    event.preventDefault();
    this.isHover.set(false);
    const files = event.dataTransfer?.files;
    if (!files?.length) { return; }

    await this.processAndSelectFiles(Array.from(files));
  }

  public handleDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isHover.set(true);
  }

  public handleDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isHover.set(false);
  }

  public togglePasteMode(): void {
    this.isPasteMode.update(i => !i);
    this.isPasteMode() &&
      this.snackbarService.showMessage({
        message: contentPaste.text,
        type: 'info'
      });
  }

  public handleFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input?.files;
    if (!files?.length) { return; }

    this.processAndSelectFiles(Array.from(files));
    input.value = '';
  }

  private handlePaste(event: ClipboardEvent): void {
    const files = Array.from(event.clipboardData?.items || [])
      .filter(item => item.kind === 'file')
      .map(item => item.getAsFile())
      .filter(Boolean) as Array<File>;

    if (!files.length) { return; }

    event.preventDefault();
    this.processAndSelectFiles(files);
    this.isPasteMode.set(false);
  }

  private processAndSelectFiles(files: Array<File>): void {
    const validateAndFilterFiles = this.delayedFileUploadCardService.validateAndFilterFiles({
      files: files,
      allowedExtensions: this.allowedExtensions(),
      isMultiple: this.multiple(),
      selectedFiles: this.selectedFiles()
    });
    this.selectedFiles.set(validateAndFilterFiles);
    this.filesSelectedForUpload.emit(this.selectedFiles());
  }

  public getAcceptString(): string {
    if (!this.allowedExtensions().length) { return '*'; }

    return this.allowedExtensions().map(ext => `.${ext}`).join(',');
  }

  public deleteFile(index: number): void {
    this.selectedFiles.update(data => {
      const newData = [ ...data ];
      newData.splice(index, 1);
      return newData;
    });
    this.filesSelectedForUpload.emit(this.selectedFiles());
  }
}
